import { datadogLogs } from '@datadog/browser-logs'

import { isProduction } from './appEnvironment'

/**
 * Safely handles different argument patterns for DataDog logging
 * Only executes in browser environment to prevent SSR/SSG issues
 *
 * @param logger - The DataDog logger function to use (info, warn, error)
 * @param arguments_ - Arguments passed to the logging function
 * @param defaultMessage - Default message to use if no arguments are provided
 */
type DatadogStatus = 'info' | 'warn' | 'error'

const safeDatadogLog = <T extends unknown[]>(
  status: DatadogStatus,
  arguments_: T,
  defaultMessage = 'No message provided',
): void => {
  // Only execute Datadog logs in browser environment
  if (typeof window === 'undefined') {
    return
  }

  const [first, second] = arguments_
  const message = String(first ?? defaultMessage)
  const error = second instanceof Error ? second : undefined

  // Datadog expects: message: string, context?: object
  const context: { status: DatadogStatus; error?: Error } = { status }
  if (error) {
    context.error = error
  }
  datadogLogs.logger.log(message, context)
}

/**
 * Enhanced logging function that only outputs messages in development environments.
 *
 * This function wraps the standard console.log and only executes in non-production
 * environments, preventing debug logs from appearing in production. It passes through
 * all arguments to console.log, maintaining the same signature and functionality.
 *
 * Usage:
 *   import { log } from './log';
 *
 *   log('User logged in', userData);
 *   log('API response:', response);
 *
 * @param args - Any arguments that would normally be passed to console.log
 */
export const log = <T extends unknown[]>(...arguments_: T): void => {
  safeDatadogLog('info', arguments_)

  if (!isProduction()) {
    // eslint-disable-next-line no-console
    console.log(...arguments_)
  }
}

/**
 * Enhanced error logging function that only outputs error messages in development environments.
 *
 * This function wraps the standard console.error and only executes in non-production
 * environments, preventing error logs from appearing in production. It passes through
 * all arguments to console.error, maintaining the same signature and functionality.
 *
 * Usage:
 *   import { logError } from './log';
 *
 *   logError('Failed to fetch data', error);
 *
 * @param args - Any arguments that would normally be passed to console.error
 */
export const logError = <T extends unknown[]>(...arguments_: T): void => {
  safeDatadogLog('error', arguments_, 'Unknown error occurred')

  if (!isProduction()) {
    console.error(...arguments_)
  }
}

/**
 * Enhanced warning logging function that only outputs warning messages in development environments.
 *
 * This function wraps the standard console.warn and only executes in non-production
 * environments, preventing warning logs from appearing in production. It passes through
 * all arguments to console.warn, maintaining the same signature and functionality.
 *
 * Usage:
 *   import { logWarning } from './log';
 *
 *   logWarning('Deprecated API usage', details);
 *
 * @param args - Any arguments that would normally be passed to console.warn
 */
export const logWarning = <T extends unknown[]>(...arguments_: T): void => {
  safeDatadogLog('warn', arguments_, 'Warning occurred')

  if (!isProduction()) {
    console.warn(...arguments_)
  }
}
